import { useState } from 'react';
import type { SATDomain } from '../data/satCurriculumData';

interface Props {
  domain: SATDomain;
  sectionColor: string;
}

export default function SATDomainCard({ domain, sectionColor }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [openTopicId, setOpenTopicId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'topics' | 'skills' | 'tips'>('topics');

  const colorMap: Record<string, { badge: string; border: string; bg: string; text: string; btn: string }> = {
    emerald: {
      badge: 'bg-emerald-100 text-emerald-700',
      border: 'border-emerald-200',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      btn: 'bg-emerald-600 hover:bg-emerald-700',
    },
    teal: {
      badge: 'bg-teal-100 text-teal-700',
      border: 'border-teal-200',
      bg: 'bg-teal-50',
      text: 'text-teal-700',
      btn: 'bg-teal-600 hover:bg-teal-700',
    },
    cyan: {
      badge: 'bg-cyan-100 text-cyan-700',
      border: 'border-cyan-200',
      bg: 'bg-cyan-50',
      text: 'text-cyan-700',
      btn: 'bg-cyan-600 hover:bg-cyan-700',
    },
    amber: {
      badge: 'bg-amber-100 text-amber-700',
      border: 'border-amber-200',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      btn: 'bg-amber-600 hover:bg-amber-700',
    },
    rose: {
      badge: 'bg-rose-100 text-rose-700',
      border: 'border-rose-200',
      bg: 'bg-rose-50',
      text: 'text-rose-700',
      btn: 'bg-rose-600 hover:bg-rose-700',
    },
    pink: {
      badge: 'bg-pink-100 text-pink-700',
      border: 'border-pink-200',
      bg: 'bg-pink-50',
      text: 'text-pink-700',
      btn: 'bg-pink-600 hover:bg-pink-700',
    },
    fuchsia: {
      badge: 'bg-fuchsia-100 text-fuchsia-700',
      border: 'border-fuchsia-200',
      bg: 'bg-fuchsia-50',
      text: 'text-fuchsia-700',
      btn: 'bg-fuchsia-600 hover:bg-fuchsia-700',
    },
    violet: {
      badge: 'bg-violet-100 text-violet-700',
      border: 'border-violet-200',
      bg: 'bg-violet-50',
      text: 'text-violet-700',
      btn: 'bg-violet-600 hover:bg-violet-700',
    },
  };

  const c = colorMap[domain.color] || colorMap['emerald'];

  const difficultyColor = (d: string) => {
    if (d === 'Foundation') return 'bg-green-100 text-green-700';
    if (d === 'Intermediate') return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className={`bg-white rounded-2xl border ${c.border} shadow-sm overflow-hidden transition-all duration-300`}>
      {/* Header */}
      <div
        className={`p-6 cursor-pointer ${c.bg} flex items-start justify-between gap-4`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${c.btn} flex-shrink-0`}>
            <i className={`${domain.icon} text-white text-xl`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-gray-900">{domain.name}</h3>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>
                {domain.percentage} of section
              </span>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {domain.questionRange}
              </span>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{domain.overview}</p>
          </div>
        </div>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full ${c.bg} flex-shrink-0 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}>
          <i className={`ri-arrow-down-s-line text-xl ${c.text}`}></i>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-6 border-t border-gray-100">
          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
            {(['topics', 'skills', 'tips'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                  activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'topics' ? 'Topics' : tab === 'skills' ? 'Key Skills' : 'Test Tips'}
              </button>
            ))}
          </div>

          {/* Topics Tab */}
          {activeTab === 'topics' && (
            <div className="space-y-3">
              {domain.topics.map((topic) => (
                <div key={topic.id} className={`border ${c.border} rounded-xl overflow-hidden`}>
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenTopicId(openTopicId === topic.id ? null : topic.id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <i className="ri-checkbox-blank-circle-fill text-xs text-gray-300 flex-shrink-0"></i>
                      <span className="font-semibold text-gray-800 text-sm">{topic.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${difficultyColor(topic.difficulty)}`}>
                        {topic.difficulty}
                      </span>
                      <span className="text-xs text-gray-400 hidden sm:block">~{topic.questionCount} Qs</span>
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${openTopicId === topic.id ? 'rotate-180' : ''}`}></i>
                    </div>
                  </div>
                  {openTopicId === topic.id && (
                    <div className={`px-4 pb-4 ${c.bg}`}>
                      <ul className="space-y-2">
                        {topic.subtopics.map((sub, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <i className={`ri-arrow-right-s-line ${c.text} flex-shrink-0 mt-0.5`}></i>
                            <span>{sub}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Key Skills Tab */}
          {activeTab === 'skills' && (
            <ul className="space-y-3">
              {domain.keySkills.map((skill, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full ${c.btn} flex-shrink-0 mt-0.5`}>
                    <span className="text-white text-xs font-bold">{i + 1}</span>
                  </div>
                  <span className="text-gray-700 text-sm leading-relaxed">{skill}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Test Tips Tab */}
          {activeTab === 'tips' && (
            <ul className="space-y-3">
              {domain.testTips.map((tip, i) => (
                <li key={i} className={`flex items-start gap-3 p-3 rounded-xl ${c.bg}`}>
                  <i className="ri-lightbulb-fill text-amber-500 flex-shrink-0 mt-0.5 text-lg"></i>
                  <span className="text-gray-700 text-sm leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
